import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { canonicalExerciseId } from '@/lib/exercise'

export interface LibraryExercise {
  id: string
  name: string
  aliases: string[] | null
  category: string | null
  subcategory: string | null
  primary_muscles: string | null
  equipment: string | null
  is_plyometric: boolean | null
}

interface LibraryValue {
  exercises: LibraryExercise[]
  loading: boolean
  /** Map any exercise name/slug/id onto the canonical library id. */
  resolveId: (exercise: { exercise_id?: string | null; name: string }) => string
  /** Fuzzy search over name + aliases + category. */
  search: (query: string, limit?: number) => LibraryExercise[]
  byId: (id: string) => LibraryExercise | undefined
}

const Ctx = createContext<LibraryValue | undefined>(undefined)

export function ExerciseLibraryProvider({ children }: { children: ReactNode }) {
  const { data: exercises = [], isLoading } = useQuery({
    queryKey: ['exercise-library'],
    enabled: supabaseReady,
    staleTime: 60 * 60 * 1000, // library rarely changes
    queryFn: async (): Promise<LibraryExercise[]> => {
      const { data, error } = await supabase
        .from('exercises')
        .select('id,name,aliases,category,subcategory,primary_muscles,equipment,is_plyometric')
        .order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as LibraryExercise[]
    },
  })

  const value = useMemo<LibraryValue>(() => {
    const idSet = new Set(exercises.map((e) => e.id))
    // alias/slug → canonical id (first entry wins on collision)
    const aliasMap = new Map<string, string>()
    for (const e of exercises) {
      const add = (k: string) => {
        if (k && !aliasMap.has(k)) aliasMap.set(k, e.id)
      }
      add(canonicalExerciseId(e.name))
      add(e.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))
      for (const a of e.aliases ?? []) add(a)
    }

    const resolveId: LibraryValue['resolveId'] = (exercise) => {
      const explicit = exercise.exercise_id?.trim()
      if (explicit && idSet.has(explicit)) return explicit // already a library id
      if (explicit && aliasMap.has(explicit)) return aliasMap.get(explicit)!
      const slug = canonicalExerciseId(exercise.name)
      if (aliasMap.has(slug)) return aliasMap.get(slug)!
      // Unknown movement — fall back to the deterministic slug so it still
      // tracks consistently with itself across programs.
      return explicit || slug
    }

    const search: LibraryValue['search'] = (query, limit = 30) => {
      const q = query.trim().toLowerCase()
      if (!q) return exercises.slice(0, limit)
      const scored: { e: LibraryExercise; score: number }[] = []
      for (const e of exercises) {
        const name = e.name.toLowerCase()
        let score = 0
        if (name === q) score = 100
        else if (name.startsWith(q)) score = 80
        else if (name.includes(q)) score = 60
        else if ((e.aliases ?? []).some((a) => a.includes(q))) score = 40
        else if ((e.category ?? '').toLowerCase().includes(q)) score = 20
        if (score) scored.push({ e, score })
      }
      return scored
        .sort((a, b) => b.score - a.score || a.e.name.localeCompare(b.e.name))
        .slice(0, limit)
        .map((s) => s.e)
    }

    const byIdMap = new Map(exercises.map((e) => [e.id, e]))
    return { exercises, loading: isLoading, resolveId, search, byId: (id) => byIdMap.get(id) }
  }, [exercises, isLoading])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useExerciseLibrary() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useExerciseLibrary must be used within ExerciseLibraryProvider')
  return ctx
}
